'use client';

import { Box, Divider, Stack, Typography } from '@mui/material';
import { EonAccountStatus } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import { redirect, usePathname } from 'next/navigation';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { Icon } from '@/components/shared/icon';
import { Link } from '@/components/shared/link';
import { UserCircle } from '@/components/users/userBadge';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useUser } from '@/contexts/useUser';
import { getDocsUrl } from '@/docs/docsUrls';

import {
  ASSISTANT_HEIGHT,
  ASSISTANT_MARGIN_BOTTOM,
  AssistantMenuItem,
  NavigationMenuItem,
  MoreMenuItem,
  NavigationActions,
  type OverflowMenuItem,
} from './components';
import { HelpButton } from './help';
import { UserBox } from './userBox';

import { EONLogo } from '../shared/eonLogo';

const assistantKey = 'assistant';
const dashboardKey = 'dashboard';
const inventoryKey = 'inventory';
const saasInventoryKey = 'saas-inventory';
const searchKey = 'search';
const jobsKey = 'jobs';
const settingsKey = 'settings';
const storageGatewayKey = 'storage-gateway';
const logSpaceKey = 'log-space';
const lakehouseKey = 'lakehouse';
// Last items in this array will be hidden first when overflow occurs
const itemsPriority = [
  assistantKey,
  dashboardKey,
  inventoryKey,
  saasInventoryKey,
  searchKey,
  jobsKey,
  settingsKey,
  storageGatewayKey,
  logSpaceKey,
  lakehouseKey,
];

const MENU_ITEM_HEIGHT = 48;
const MENU_ITEM_GAP = 4;
const ASSISTANT_ITEM_HEIGHT = ASSISTANT_HEIGHT + ASSISTANT_MARGIN_BOTTOM;
const DOCS_SECTION_HEIGHT = 60;
const DOMAIN_LABEL_HEIGHT = 60;
const TOP_SECTION_PADDING = 24;
const BOTTOM_SECTION_PADDING = 60;
const BOTTOM_SECTION_HEIGHT =
  DOCS_SECTION_HEIGHT + DOMAIN_LABEL_HEIGHT + BOTTOM_SECTION_PADDING;

export const Navigation = () => {
  const dal = useDAL();
  const { data: projects } = dal.projects.useList();
  const { dlsgFrontend, assistantEnabled, LlgFrontend } = useFeatureFlags();
  const { demoSettings } = useEnvironment();
  const pathname = usePathname();
  const { user, isEmployee } = useUser();
  const [demoSettingsMenuOpen, setDemoSettingsMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [userBoxOpened, setUserBoxOpened] = useState(false);

  const forceOpen = demoSettingsMenuOpen || moreMenuOpen || userBoxOpened;
  const onboardingMode =
    user?.eonAccount.status === EonAccountStatus.EON_ACCOUNT_STATUS_ONBOARDING;
  const registrationMode =
    user?.eonAccount.status ===
    EonAccountStatus.EON_ACCOUNT_STATUS_REGISTRATION;
  const { isAuthorized, rolesMap, isGlobalRole } = useRoles();

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [overflowKeys, setOverflowKeys] = useState(new Set<string>());

  const hasAccountRoles = Object.keys(user?.roleToProjectsMappings || {}).some(
    (roleId) => {
      const role = rolesMap[roleId];
      return isGlobalRole(role);
    }
  );

  const allMenuItems = useMemo((): OverflowMenuItem[] => {
    const items: OverflowMenuItem[] = [];

    if (demoSettings.ai || assistantEnabled) {
      items.push({
        key: assistantKey,
        href: '/assistant',
        title: 'Search & explore your data',
        iconClass: '',
        isAssistant: true,
      });
    }

    items.push({
      key: dashboardKey,
      href: '/dashboard',
      iconClass: 'material-symbols-team-dashboard-outline',
      title: 'Dashboard',
      disabled: !isAuthorized('read:dashboard'),
    });

    items.push({
      key: inventoryKey,
      href: '/inventory',
      iconClass: 'material-symbols-inventory-2-outline',
      title: 'Inventory',
      disabled: !isAuthorized('read:inventory'),
    });

    if (demoSettings.googleWorkspace) {
      items.push({
        key: saasInventoryKey,
        href: '/saas-inventory',
        iconClass: 'material-symbols-cloud-outline',
        title: 'SaaS Inventory',
        disabled: !isAuthorized('read:inventory'),
      });
    }

    if (!(demoSettings.ai || assistantEnabled)) {
      items.push({
        key: searchKey,
        href: '/search',
        iconClass: 'material-symbols-search',
        title: 'Search',
        disabled: !isAuthorized('read:db') && !isAuthorized('read:files'),
      });
    }

    items.push({
      key: jobsKey,
      href: '/jobs',
      iconClass: 'material-symbols-checklist-rounded',
      title: 'Jobs',
      disabled: !isAuthorized('read:jobs'),
    });

    items.push({
      key: settingsKey,
      href: '/settings',
      iconClass: 'material-symbols-settings-outline-rounded',
      title: 'Backup settings',
      disabled: !isAuthorized('read:backup_settings'),
    });

    if (dlsgFrontend) {
      items.push({
        key: storageGatewayKey,
        href: '/storage-gateway',
        iconClass: 'material-symbols-door-open-outline-rounded',
        title: 'Storage gateway',
        disabled: !isAuthorized('read:policies'),
      });
    }

    if (demoSettings.logSpace || LlgFrontend) {
      items.push({
        key: logSpaceKey,
        href: '/log-space',
        iconClass: 'material-symbols-planet-outline',
        title: 'Log space',
      });
    }

    if (demoSettings.lakehouse) {
      items.push({
        key: lakehouseKey,
        href: '/lakehouse',
        iconClass: 'material-symbols-database-outline',
        title: 'Data lake management',
      });
    }

    return items;
  }, [demoSettings, assistantEnabled, dlsgFrontend, LlgFrontend, isAuthorized]);

  // Find the currently selected item key based on pathname
  const selectedKey = useMemo(() => {
    for (const item of allMenuItems) {
      if (pathname.startsWith(item.href)) {
        return item.key;
      }
    }
    return null;
  }, [allMenuItems, pathname]);

  // Calculate which items should overflow
  const calculateOverflow = useCallback(() => {
    if (!containerRef.current || !headerRef.current) return;

    const containerHeight = containerRef.current.clientHeight;
    const headerHeight = headerRef.current.clientHeight;
    const availableHeight =
      containerHeight -
      headerHeight -
      TOP_SECTION_PADDING -
      BOTTOM_SECTION_HEIGHT;

    // Calculate total height needed for all items (heights + gaps between them)
    // starting from 1 so there will be at least one item space reserved.
    let totalNeeded = 1;
    for (const item of allMenuItems) {
      totalNeeded += item.isAssistant
        ? ASSISTANT_ITEM_HEIGHT
        : MENU_ITEM_HEIGHT;
    }
    totalNeeded += Math.max(0, allMenuItems.length - 1) * MENU_ITEM_GAP;

    // If everything fits, no overflow needed
    if (totalNeeded <= availableHeight) {
      setOverflowKeys(new Set());
      return;
    }

    // We need overflow - reserve space for "more" button (height + gap)
    const availableWithMore =
      availableHeight - MENU_ITEM_HEIGHT - MENU_ITEM_GAP;

    // // Sort items by priority (lowest priority first = first to hide)
    // // Items not in priority list are treated as lowest priority
    const sortedByHidePriority = [...allMenuItems].sort((a, b) => {
      const aPriority = itemsPriority.indexOf(a.key);
      const bPriority = itemsPriority.indexOf(b.key);
      const aScore = aPriority === -1 ? Infinity : aPriority;
      const bScore = bPriority === -1 ? Infinity : bPriority;
      // Higher score = lower priority = hide first
      return bScore - aScore;
    });

    // Determine which items to hide
    const keysToHide = new Set<string>();
    let currentHeight = totalNeeded;

    for (const item of sortedByHidePriority) {
      if (currentHeight <= availableWithMore) break;

      // Never hide the selected item
      if (item.key === selectedKey) continue;

      const itemHeight =
        (item.isAssistant ? ASSISTANT_ITEM_HEIGHT : MENU_ITEM_HEIGHT) +
        MENU_ITEM_GAP;
      keysToHide.add(item.key);
      currentHeight -= itemHeight;
    }

    setOverflowKeys(keysToHide);
  }, [allMenuItems, selectedKey]);

  // Setup ResizeObserver
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => {
      calculateOverflow();
    });

    observer.observe(containerRef.current);
    calculateOverflow(); // Initial calculation

    return () => observer.disconnect();
  }, [calculateOverflow]);

  // Recalculate when menu items or selected item changes
  useEffect(() => {
    calculateOverflow();
  }, [calculateOverflow, allMenuItems, selectedKey]);

  const { visibleItems, overflowItems } = useMemo(() => {
    const visible: OverflowMenuItem[] = [];
    const overflow: OverflowMenuItem[] = [];

    for (const item of allMenuItems) {
      if (overflowKeys.has(item.key)) {
        overflow.push(item);
      } else {
        visible.push(item);
      }
    }

    return { visibleItems: visible, overflowItems: overflow };
  }, [allMenuItems, overflowKeys]);

  useEffect(() => {
    if (
      !user ||
      user.givenName?.toLowerCase().includes('eon support') ||
      isEmployee
    )
      return;

    if (onboardingMode && !pathname.includes('/global-management')) {
      redirect('/global-management');
    } else if (registrationMode) {
      redirect('/onboarding/in-progress');
    }
  }, [onboardingMode, pathname, registrationMode, user, isEmployee]);

  return (
    <Box
      data-testid='navigation'
      // setting the theme for that component to always be dark.
      className={classNames('dark', {
        open: forceOpen,
      })}
      sx={{
        minWidth: '48px',
        maxWidth: '48px',
        position: 'sticky',
        height: '100%',
        zIndex: 1000,

        '& .slide-left, .logo-link *, .nav-collapsed-only, .nav-item-icon': {
          transition: `opacity 500ms ease-in-out, padding-left 300ms ease-in-out, margin-left 300ms ease-in-out, visibility 500ms ease-in-out`,
        },

        '& .navigation-actions': {
          transition: `opacity 150ms ease-in-out, visibility 150ms ease-in-out`,
        },
        // delay appearance until nav is ~half open; closing has no delay so it's gone before the panel collapses
        '&:hover .navigation-actions, &.open .navigation-actions': {
          transition: `opacity 150ms ease-in-out 150ms, visibility 150ms ease-in-out 150ms`,
        },

        '& a': {
          display: 'flex',
          alignItems: 'center',
          color: 'white',
          height: '48px',
          position: 'relative',
          width: '100%',
        },

        '& .nav-collapsed-only': {
          opacity: 1,
        },

        '& .user-box, .navigation-actions, a': {
          pointerEvents: 'none',
        },

        '& .nav-item-icon': {
          marginLeft: '12px',
        },

        '&:hover, &.open': {
          '& .nav-collapsed-only': {
            opacity: 0,
          },

          '& .nav-item-icon': {
            marginLeft: '18px',
          },

          '& > div': {
            minWidth: '308px',
          },

          '& .slide-left': {
            opacity: '1 !important',
            visibility: 'visible !important',
            paddingLeft: '3px',
            pointerEvents: 'all',

            a: {
              pointerEvents: 'all',
            },
          },

          '& .logo-link img': {
            marginLeft: '19px !important',
          },
        },
      }}
    >
      <Stack
        ref={containerRef}
        justifyContent='space-between'
        sx={{
          overflow: 'hidden',
          backgroundColor: 'var(--mui-palette-background-primary)',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          height: '100%',
          minWidth: '48px',
          transition: 'min-width 300ms ease-in-out',
        }}
      >
        <Stack gap={`${TOP_SECTION_PADDING}px`}>
          <Stack
            ref={headerRef}
            className={classNames({
              'min-h-[200px]': !hasAccountRoles,
              'min-h-[280px]': hasAccountRoles,
              'min-h-[300px]': (projects?.projects || []).length > 1,
            })}
          >
            <Stack
              direction='row'
              alignItems='start'
              className='relative'
              sx={{ paddingTop: '24px' }}
            >
              <Link href='/' className='slide-left logo-link'>
                <>
                  <EONLogo className='ml-[-16px]' />
                  <Box className='w-[24px] h-[24px] absolute left-[-10px] bg-[var(--mui-palette-background-primary)]' />
                </>
              </Link>
              <Stack
                alignItems='center'
                direction='row'
                sx={{
                  position: 'absolute',
                  right: '24px',
                  top: '24px',
                  height: '48px',
                }}
              >
                <NavigationActions
                  demoSettingsMenuOpen={demoSettingsMenuOpen}
                  onDemoSettingsMenuOpenChange={setDemoSettingsMenuOpen}
                />
              </Stack>
            </Stack>
            <Box className='relative'>
              <Stack
                className='w-[48px] h-[48px] nav-collapsed-only'
                alignItems='center'
                justifyContent='center'
              >
                <UserCircle user={user} />
              </Stack>
              <UserBox
                hasAccountRoles={hasAccountRoles}
                projects={projects?.projects}
                setForceOpen={setUserBoxOpened}
              />
            </Box>
          </Stack>
          <Stack gap='4px'>
            {visibleItems.map((item) =>
              item.isAssistant ? (
                <AssistantMenuItem
                  key={item.key}
                  href={item.href}
                  title={item.title}
                  selected={pathname.startsWith(item.href)}
                />
              ) : (
                <NavigationMenuItem
                  key={item.key}
                  href={item.href}
                  iconClass={item.iconClass}
                  title={item.title}
                  disabled={item.disabled}
                  selected={!item.disabled && pathname.startsWith(item.href)}
                />
              )
            )}
            {overflowItems.length > 0 && (
              <MoreMenuItem
                items={overflowItems}
                open={moreMenuOpen}
                onOpenChange={setMoreMenuOpen}
              />
            )}
          </Stack>
        </Stack>
        <Stack className='relative justify-end pb-[12px]'>
          <Box className='nav-collapsed-only'>
            <NavigationMenuItem
              key='docs'
              href={getDocsUrl('root')}
              iconClass='material-symbols-help-outline-rounded'
              title=''
            />
          </Box>
          <Box className='w-full slide-left collapse opacity-0 absolute bottom-[12px]'>
            <Stack>
              <Divider
                sx={{
                  borderColor: 'var(--mui-palette-divider)',
                }}
              />
              <Stack
                justifyContent='space-between'
                alignItems='center'
                paddingX='24px'
                divider={
                  <Divider
                    orientation='vertical'
                    flexItem
                    sx={{
                      marginY: '24px',
                      borderColor: 'var(--mui-palette-divider)',
                    }}
                  />
                }
                direction='row'
                gap='12px'
              >
                <HelpButton />
                <Link
                  href={getDocsUrl('root')}
                  target='_blank'
                  className='w-auto'
                >
                  <ButtonWithIcon
                    text='Eon Docs'
                    data-testid='eon-docs'
                    icon='material-symbols-book-ribbon-outline-rounded'
                    sx={{
                      color: 'white',
                      '&:not(.Mui-disabled):hover': {
                        color: 'white',
                        backgroundColor: 'var(--mui-palette-primary-hover)',
                      },
                    }}
                  />
                </Link>
              </Stack>
              <Divider
                sx={{
                  borderColor: 'var(--mui-palette-divider)',
                }}
              />
            </Stack>
            <CompanyData />
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

const CompanyData = () => {
  const { eonAccount } = useUser();
  const label = (eonAccount?.labels || []).at(0) || eonAccount?.domain;

  return (
    <Stack
      padding='24px 24px 12px 16px'
      direction='row'
      alignItems='center'
      gap='12px'
    >
      {/* todo: add company icon once supported in server */}
      <Icon
        iconClass='material-symbols-image-outline-rounded'
        color='var(--mui-palette-text-disabled)'
      />
      <Typography
        noWrap
        color='var(--mui-palette-text-disabled)'
        variant='body1'
      >
        {label}
      </Typography>
    </Stack>
  );
};
